import { STAKE_CONSTANTS } from "./stake-constants";

/**
 * Returns the DAO year index (1-based) at a given timestamp.
 * Year 1 starts at launchTime. Year 2 starts at launchTime + 365 days, etc.
 */
export function daoYearAt(tsSec: bigint, launchTime: bigint): number {
  if (tsSec < launchTime) return 1;
  const diff = tsSec - launchTime;
  return Number(diff / STAKE_CONSTANTS.REWARD_DECAY_PERIOD_SECONDS) + 1;
}

/**
 * Returns remaining time in the current DAO year.
 */
export function remainingInCurrentYear(
  nowSec: bigint,
  launchTime: bigint,
): { days: number; hours: number; minutes: number; seconds: number } {
  const diff = nowSec >= launchTime ? nowSec - launchTime : 0n;
  const completedYears = diff / STAKE_CONSTANTS.REWARD_DECAY_PERIOD_SECONDS;
  const nextBoundary =
    launchTime +
    (completedYears + 1n) * STAKE_CONSTANTS.REWARD_DECAY_PERIOD_SECONDS;
  const remSec = Number(nextBoundary - nowSec);
  return {
    days: Math.floor(remSec / 86400),
    hours: Math.floor((remSec % 86400) / 3600),
    minutes: Math.floor((remSec % 3600) / 60),
    seconds: remSec % 60,
  };
}

/**
 * Returns the start/end timestamps (in seconds) for a given DAO year index.
 * yearIndex is 1-based.
 */
export function yearBoundaries(
  yearIndex: number,
  launchTime: bigint,
): { start: bigint; end: bigint } {
  const n = BigInt(yearIndex - 1);
  return {
    start: launchTime + n * STAKE_CONSTANTS.REWARD_DECAY_PERIOD_SECONDS,
    end: launchTime + (n + 1n) * STAKE_CONSTANTS.REWARD_DECAY_PERIOD_SECONDS,
  };
}

const PERSIAN_ORDINALS = [
  "اول",
  "دوم",
  "سوم",
  "چهارم",
  "پنجم",
  "ششم",
  "هفتم",
  "هشتم",
  "نهم",
  "دهم",
] as const;

/**
 * Returns the Persian ordinal label for a 1-based DAO year index.
 * 1 → "سال اول", 10 → "سال دهم", 11+ → "سال 11" (Latin digit fallback).
 */
export function persianYearLabel(yearIndex: number): string {
  const ord = PERSIAN_ORDINALS[yearIndex - 1];
  return ord ? `سال ${ord}` : `سال ${yearIndex}`;
}

/**
 * Cumulative reward-reduction percentage for a given 1-based DAO year ordinal.
 *
 * Mirrors the StakeMeta.calculateReward decay loop:
 *   for (j = 0; j < years_passed; j++)
 *     temp_reward = (temp_reward * (100 - REWARD_DECAY_FACTOR)) / 100;
 *
 * For DAO year ordinal `n`, years_passed = n - 1, and the surviving
 * reward fraction is `(1 - decayFactor/100)^(n-1)`. The cumulative
 * reduction shown to the user is `1 - survivingFraction`, expressed
 * as a percent.
 *
 * Locked fixtures (decayFactor=20):
 *   year 1 → 0%
 *   year 2 → 20%
 *   year 3 → 36%
 *   year 4 → 48.8%
 *   year 5 → 59.04%
 *
 * @param yearIndex 1-based DAO year ordinal.
 * @param decayFactor Per-year reduction factor (whole-number percent, e.g. 20).
 * @returns Cumulative reduction as a number (e.g. 36 for year 3).
 */
export function cumulativeReductionPercent(
  yearIndex: number,
  decayFactor: bigint,
): number {
  if (yearIndex <= 1) return 0;
  const survivingPerStep = (100 - Number(decayFactor)) / 100;
  const survivingFraction = survivingPerStep ** (yearIndex - 1);
  return (1 - survivingFraction) * 100;
}

/**
 * Formatted cumulative reduction percentage. Trims trailing zeros and
 * caps at one decimal so year 4 reads "48.8" not "48.80000000000001".
 */
export function formatCumulativeReductionPercent(
  yearIndex: number,
  decayFactor: bigint,
): string {
  const value = cumulativeReductionPercent(yearIndex, decayFactor);
  const rounded = Math.round(value * 10) / 10;
  const fixed = rounded.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}
